import type { JSX } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { CARDS_ASPECT_RATIO } from 'blocks/Cards/Card.h'
import type { COLOR, GRID } from 'vars/index'

import React, { useRef } from 'react'
import { TextBox, TextBoxTypes } from 'atoms/TextBox'
import { CardsInRow } from 'containers/RowContainer/RowContainer.h'
import { FIXED_INDENTS, LAYOUT } from 'vars/index'
import { LazyLoad } from './components/LazyLoad/LazyLoad'
import { GridColumnGaps, GridRowGaps } from './Grid.h'
import { GridSC, GridWrapper } from './Grid.styled'
import { useGridCardsCount } from './hooks'

export interface IGridProps {
  children: React.ReactNode
  content: object[]
  cardsInRow?: CardsInRow
  rowGap?: (typeof GridRowGaps)[keyof typeof GridRowGaps]
  columnGap?: (typeof GridColumnGaps)[keyof typeof GridColumnGaps]
  title?: ITextBoxProps['title']
  colorTitle?: ITextBoxProps['color']
  fontTitle?: ITextBoxProps['font']
  maxLineTitle?: ITextBoxProps['maxLines']
  paddings?: Array<FIXED_INDENTS | LAYOUT | GRID>
  marginTitle?: Array<FIXED_INDENTS | LAYOUT | GRID>
  onLoadMoreItems: () => any
  aspectRatio?: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  tvChannelCard?: boolean
  verticalTopOffset?: LAYOUT
  backgroundColor?: COLOR
}

export const Grid = ({
  children,
  content,
  cardsInRow = CardsInRow.Horizontal_5,
  rowGap = GridRowGaps.DEFAULT,
  columnGap = GridColumnGaps.DEFAULT,
  title,
  colorTitle,
  fontTitle = TextBoxTypes.Headline3,
  maxLineTitle = 1,
  verticalTopOffset,
  paddings = [FIXED_INDENTS.INDENT_0, LAYOUT.HorizontalPaddings],
  marginTitle = [FIXED_INDENTS.INDENT_0],
  onLoadMoreItems,
  aspectRatio,
  tvChannelCard = false,
  backgroundColor,
}: IGridProps): JSX.Element => {
  const gridRef = useRef<HTMLDivElement>(null)

  const cardsCount = useGridCardsCount(cardsInRow, tvChannelCard)

  return cardsCount && content?.length > 0 && React.Children.count(children) ? (
    <GridWrapper
      paddings={paddings}
      marginTitle={marginTitle}
      verticalTopOffset={verticalTopOffset}
      backgroundColor={backgroundColor}
    >
      {title && (
        <TextBox
          color={colorTitle}
          maxLines={maxLineTitle}
          font={fontTitle}
          title={title}
        />
      )}
      <GridSC
        ref={gridRef}
        rowGap={rowGap}
        columnGap={columnGap}
        cardsCount={cardsCount}
        aspectRatio={aspectRatio!}
      >
        <LazyLoad onLoadMore={onLoadMoreItems} percentToTrigger={50}>
          {children}
        </LazyLoad>
      </GridSC>
    </GridWrapper>
  ) : (
    <></>
  )
}
